import { characteristics } from '../../data/characteristics';
import { harmonicLevels } from '../../data/harmonicLevels';
import type { Score } from '../../types';
import type { HarmonicScore } from '../../types/harmonic';

const RESOURCE_STATE_IDS = [9, 10, 11, 12];
const STABILISING_STATE_ID = 8;
const BLOCKER_STATE_IDS = [7, 6, 5, 4, 3, 2, 1];

export function buildBreakthroughPrompt(
  scores: Score[],
  harmonicScores: HarmonicScore[],
  instruction: string,
): string {
  const sortedCharacteristics = [...scores].sort((a, b) => b.normalized - a.normalized);
  const highestCharacteristic = sortedCharacteristics[0];
  const lowestCharacteristic = sortedCharacteristics[sortedCharacteristics.length - 1];
  const resourceScores = harmonicScores
    .filter(score => RESOURCE_STATE_IDS.includes(score.levelId))
    .sort((a, b) => b.normalized - a.normalized);
  const blockerScores = harmonicScores
    .filter(score => BLOCKER_STATE_IDS.includes(score.levelId))
    .sort((a, b) => b.normalized - a.normalized);
  const highestResource = resourceScores[0];
  const highestBlocker = blockerScores[0];
  const stabilisingScore = harmonicScores.find(score => score.levelId === STABILISING_STATE_ID);

  const formatCharacteristic = (score: Score | undefined): string => {
    if (!score) return 'Not available';
    const characteristic = characteristics.find(item => item.id === score.characteristicId);
    return `${characteristic?.name || score.characteristicId}: ${Math.round(score.normalized)}%`;
  };

  const formatHarmonic = (score: HarmonicScore | undefined): string => {
    if (!score) return 'Not available';
    const level = harmonicLevels.find(item => item.id === score.levelId);
    return `${level?.name || score.levelId}: ${Math.round(score.normalized)}%`;
  };

  return `
NLP OS™ BREAKTHROUGH EVALUATION DATA

═══════════════════════════════════════
CHARACTERISTIC PROFILE
═══════════════════════════════════════

All characteristics (highest to lowest):
${sortedCharacteristics.map(formatCharacteristic).join('\n')}

Highest characteristic (leverage resource): ${formatCharacteristic(highestCharacteristic)}
Lowest characteristic (development focus): ${formatCharacteristic(lowestCharacteristic)}

═══════════════════════════════════════
HARMONIC SCALE PROFILE
═══════════════════════════════════════

All Harmonic states (highest to lowest):
${[...harmonicScores].sort((a, b) => b.normalized - a.normalized).map(formatHarmonic).join('\n')}

Highest resource state (Interest and above): ${formatHarmonic(highestResource)}
Highest blocker state (Antagonism and below): ${formatHarmonic(highestBlocker)}
Rational Neutrality: ${stabilisingScore ? Math.round(stabilisingScore.normalized) : 0}%

═══════════════════════════════════════
BREAKTHROUGH EVALUATION PRINCIPLE
═══════════════════════════════════════

Use the highest blocker, lowest characteristic, and highest characteristic together. The breakthrough zone is not necessarily the lowest score alone: a high characteristic can also become a breakthrough area when a blocker suppresses, distorts, or overrides it. Treat all psychological interpretations as hypotheses to validate with the respondent.

═══════════════════════════════════════
INSTRUCTION
═══════════════════════════════════════

${instruction}
`;
}
